import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { clientSchema, type ClientFormData } from './client-schema';
import { useCreateClient, useUpdateClient, useNextClientCode } from '@/hooks/use-clients';
import { useCompanySettings } from '@/hooks/use-settings';
import type { Client } from '@erp/data-access/interfaces';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@erp/ui';
import { Button } from '@erp/ui';
import { Input } from '@erp/ui';
import { Label } from '@erp/ui';
import { Textarea } from '@erp/ui';

interface ClientFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client?: Client | null;
  onCreated?: (client: Client) => void;
}

export function ClientFormDialog({ open, onOpenChange, client, onCreated }: ClientFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = !!client;
  const createMutation = useCreateClient();
  const updateMutation = useUpdateClient();
  const { data: companySettings } = useCompanySettings();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { data: nextCode } = useNextClientCode(open && !isEdit);
  // On retient le code auto-généré pour savoir si l'utilisateur l'a modifié.
  const [autoCode, setAutoCode] = useState<string | null>(null);

  const {
    register, handleSubmit, reset, setValue, watch,
    formState: { errors, isSubmitting },
  } = useForm<ClientFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Zod v4 + RHF resolver compat
    resolver: zodResolver(clientSchema) as any,
    defaultValues: {
      code: '', name: '', type: 'company', taxId: '', address: '',
      city: '', phone: '', email: '', carRegistrationNumber: '', defaultDiscountPct: 0,
      paymentTermsDays: 0, notes: '',
    },
  });

  const type = watch('type');
  const isCompany = type === 'company';

  useEffect(() => {
    if (client) {
      reset({
        code: client.code, name: client.name, type: client.type,
        taxId: client.taxId ?? '', address: client.address ?? '',
        city: client.city ?? '', phone: client.phone ?? '',
        email: client.email ?? '', carRegistrationNumber: client.carRegistrationNumber ?? '', defaultDiscountPct: client.defaultDiscountPct,
        paymentTermsDays: client.paymentTermsDays, notes: client.notes ?? '',
      });
      setAutoCode(null);
    } else {
      reset({
        code: nextCode ?? '', name: '', type: 'company', taxId: '', address: '',
        city: '', phone: '', email: '', carRegistrationNumber: '', defaultDiscountPct: 0,
        paymentTermsDays: 0, notes: '',
      });
      setAutoCode(nextCode ?? null);
    }
    setSubmitError(null);
  }, [client, reset, open, nextCode]);

  const onSubmit = async (data: ClientFormData) => {
    setSubmitError(null);
    // Si l'utilisateur n'a pas modifié le code pré-rempli, on laisse le backend
    // consommer le compteur (code = undefined = auto-gen). Sinon on envoie la
    // valeur saisie telle quelle.
    const codeUnchanged = !isEdit && autoCode !== null && data.code === autoCode;
    const input = {
      ...data,
      code: codeUnchanged ? undefined : data.code,
      taxId: data.taxId || undefined,
      address: data.address || undefined,
      city: data.city || undefined,
      phone: data.phone || undefined,
      email: data.email || undefined,
      carRegistrationNumber: companySettings?.showClientCarNumber === false
        ? undefined
        : data.carRegistrationNumber || undefined,
      notes: data.notes || undefined,
    };
    try {
      if (isEdit && client) {
        await updateMutation.mutateAsync({ id: client.id, input });
      } else {
        const created = await createMutation.mutateAsync(input);
        onCreated?.(created);
      }
      onOpenChange(false);
    } catch (e) {
      const err = e as { code?: string; message?: string };
      if (err?.code === '23505') {
        setSubmitError(t('clients.errors.duplicateCode'));
      } else {
        setSubmitError(err?.message ?? t('common.errorSaving'));
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('clients.editClient') : t('clients.newClient')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Type — segmented control */}
          <div className="space-y-1.5">
            <Label>{t('clients.type')} *</Label>
            <div className="grid grid-cols-2 gap-0.5 rounded-md border bg-background p-0.5">
              {(['company', 'individual'] as const).map((opt) => {
                const active = type === opt;
                return (
                  <Button
                    key={opt}
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setValue('type', opt, { shouldDirty: true })}
                    className={`h-8 text-xs ${active ? 'bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary' : 'text-muted-foreground'}`}
                  >
                    {t(opt === 'company' ? 'clients.typeCompany' : 'clients.typeIndividual')}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Code + Tax ID / CIN */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="code">{t('clients.code')} *</Label>
              <Input id="code" {...register('code')} />
              {errors.code && <p className="text-xs text-destructive">{t(errors.code.message!)}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="taxId">{isCompany ? t('clients.taxId') : t('clients.cin')}</Label>
              <Input
                id="taxId"
                {...register('taxId')}
                placeholder={isCompany ? '0000000/A/M/000' : '12345678'}
              />
            </div>
          </div>

          {/* Name — Raison sociale (company) vs Nom complet (individual) */}
          <div className="space-y-1.5">
            <Label htmlFor="name">
              {isCompany ? t('clients.name') : t('clients.fullName')} *
            </Label>
            <Input id="name" {...register('name')} />
            {errors.name && <p className="text-xs text-destructive">{t(errors.name.message!)}</p>}
          </div>

          {/* Phone + Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="phone">{t('clients.phone')}</Label>
              <Input id="phone" {...register('phone')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">{t('clients.email')}</Label>
              <Input id="email" type="email" {...register('email')} />
              {errors.email && <p className="text-xs text-destructive">{t('clients.validation.emailInvalid')}</p>}
            </div>
          </div>

          {/* Address + City */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="address">{t('clients.address')}</Label>
              <Input id="address" {...register('address')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="city">{t('clients.city')}</Label>
              <Input id="city" {...register('city')} />
            </div>
          </div>

          {companySettings?.showClientCarNumber !== false && (
            <div className="space-y-1.5">
              <Label htmlFor="carRegistrationNumber">{t('clients.carRegistrationNumber')}</Label>
              <Input
                id="carRegistrationNumber"
                {...register('carRegistrationNumber')}
                placeholder={t('clients.carRegistrationPlaceholder')}
              />
            </div>
          )}

          {/* Company-only: Default discount + Payment terms */}
          {isCompany && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="defaultDiscountPct">{t('clients.defaultDiscount')}</Label>
                <Input id="defaultDiscountPct" type="number" step="0.01" {...register('defaultDiscountPct')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="paymentTermsDays">{t('clients.paymentTerms')}</Label>
                <Input id="paymentTermsDays" type="number" {...register('paymentTermsDays')} />
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="notes">{t('clients.notes')}</Label>
            <Textarea id="notes" {...register('notes')} rows={2} />
          </div>

          {submitError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {submitError}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
